/**
 * ALC-4470 fix round 1 (architect M1, m2; senior M1): "does the bill still hold this value?" in one
 * place, for the API (the resolve guards) and the web (the "do e-mail" tag on the bill page).
 *
 * Only four provenance fields map to a single bill column. The payment destinations (Chave PIX,
 * PIX copia e cola) and the vendor identity (CNPJ/CPF, name) have no column to compare, so for them
 * the answer is always "unknown" (false) and the callers do not guard on it.
 *
 * Money is compared exactly, as plain decimal text, and is never rounded: the same rule the rest of
 * the money code follows (no rounded comparison to detect a change).
 */
import { EmailBodyField, normalizeEmailBodyDocumentNumber } from './email-body-field-provenance.js';

/** The provenance fields backed by one bill column; the only ones whose held value can be checked. */
export const INTAKE_COLUMN_BACKED_FIELDS = [
  EmailBodyField.DUE_DATE,
  EmailBodyField.ISSUE_DATE,
  EmailBodyField.DOCUMENT_NUMBER,
  EmailBodyField.TOTAL_AMOUNT,
] as const;
export type IntakeColumnBackedField = (typeof INTAKE_COLUMN_BACKED_FIELDS)[number];

export function isIntakeColumnBackedField(field: string): field is IntakeColumnBackedField {
  return (INTAKE_COLUMN_BACKED_FIELDS as readonly string[]).includes(field);
}

/**
 * The bill columns the four fields read. Dates arrive as a `Date` (API) or a day / ISO string (web);
 * amounts as a number (web), a decimal string or a Prisma `Decimal` (API).
 */
export interface IntakeProvenanceHeldColumns {
  dueDate?: Date | string | null;
  billIssueDate?: Date | string | null;
  vendorBillNumber?: string | null;
  totalAmount?: unknown;
}

const DAY = /^(\d{4}-\d{2}-\d{2})/;

/** The calendar day (YYYY-MM-DD) of a Date, a day string or a full ISO timestamp; else null. */
function calendarDay(value: unknown): string | null {
  if (value == null) return null;
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  const match = DAY.exec(String(value));
  return match ? match[1]! : null;
}

const PLAIN_DECIMAL = /^-?\d+(\.\d+)?$/;

/** `1750.100000` and `1750.1` and `1750.10` are one amount; anything that is not plain decimal text is not. */
function canonicalDecimal(value: unknown): string | null {
  if (value == null) return null;
  const text = String(value).trim();
  if (!PLAIN_DECIMAL.test(text)) return null;
  const negative = text.startsWith('-');
  const [integerPart = '', fractionPart = ''] = (negative ? text.slice(1) : text).split('.');
  const integer = integerPart.replace(/^0+(?=\d)/, '');
  const fraction = fractionPart.replace(/0+$/, '');
  const canonical = fraction ? `${integer}.${fraction}` : integer;
  return negative && canonical !== '0' ? `-${canonical}` : canonical;
}

/**
 * Whether the bill's column for `field` equals `value` (the e-mail value, the nota value or the
 * recorded value, whichever the caller is checking).
 */
export function intakeProvenanceBillHoldsValue(
  field: string,
  value: unknown,
  held: IntakeProvenanceHeldColumns,
): boolean {
  if (value == null) return false;
  switch (field) {
    case EmailBodyField.DUE_DATE: {
      const wanted = calendarDay(value);
      return wanted != null && wanted === calendarDay(held.dueDate);
    }
    case EmailBodyField.ISSUE_DATE: {
      const wanted = calendarDay(value);
      return wanted != null && wanted === calendarDay(held.billIssueDate);
    }
    case EmailBodyField.DOCUMENT_NUMBER: {
      if (held.vendorBillNumber == null) return false;
      const wanted = normalizeEmailBodyDocumentNumber(String(value));
      return wanted !== '' && wanted === normalizeEmailBodyDocumentNumber(held.vendorBillNumber);
    }
    case EmailBodyField.TOTAL_AMOUNT: {
      const wanted = canonicalDecimal(value);
      return wanted != null && wanted === canonicalDecimal(held.totalAmount);
    }
    default:
      return false;
  }
}
